import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { Video } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useCreateLiveDraft,
  useLiveListQuery,
} from "@/modules/cf-live/presentation/api/queries";
import { usePosWarehousesQuery } from "@/modules/cf-feed/presentation/api/queries";
import { LiveListTable } from "@/modules/cf-live/presentation/ui/LiveListTable";
import { redirectToFacebookOAuth } from "@/modules/facebook/presentation/api/client";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { FacebookPageCombobox } from "@/modules/facebook/presentation/ui/FacebookPageCombobox";
import { QueryState } from "@/shared/ui/QueryState";
import { useWorkspaceEnabled } from "../api/workspace-client";

export function LiveListPage() {
  const nav = useNavigate();
  const [pageId, setPageId] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<
    "all" | "draft" | "live" | "ended" | "other"
  >("all");
  const debouncedSearch = useDebounceCallback((value: string) => {
    setQ(value.trim());
  }, 350);
  const pagesQuery = useFacebookPagesQuery();
  const listQuery = useLiveListQuery({
    pageId,
    q: q || undefined,
    status: status === "all" ? undefined : status,
  });
  const createDraft = useCreateLiveDraft();
  const workspaceEnabled = useWorkspaceEnabled().data?.enabled === true;
  const warehouseNames = new Map(
    (usePosWarehousesQuery().data?.items ?? []).map((warehouse) => [
      warehouse.id,
      warehouse.name,
    ]),
  );

  const activePages =
    pagesQuery.data?.pages.filter(
      (p) => workspaceEnabled || p.status !== "disconnected",
    ) ?? [];

  const handleNewLive = async () => {
    const page = activePages.find((p) => p.id === pageId) ?? activePages[0];
    if (!page) {
      toast.error("ກະລຸນາເຊື່ອມ Facebook Page ກ່ອນ");
      return;
    }
    if (
      !workspaceEnabled &&
      (page.tokenStatus === "expired" ||
        page.tokenStatus === "invalid" ||
        page.tokenStatus === "revoked")
    ) {
      toast.error("ກະລຸນາເຊື່ອມຕໍ່ Facebook ໃໝ່");
      return;
    }

    try {
      const { draft } = await createDraft.mutateAsync({ pageId: page.id });
      await nav({
        to: "/app/live/new",
        search: { draftId: draft.id, pageId: page.id },
      });
    } catch (e) {
      toast.error("ສ້າງຮ່າງ Live ບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const handleConnectFacebook = async () => {
    try {
      await redirectToFacebookOAuth("/app/live");
    } catch (e) {
      toast.error("ເລີ່ມ OAuth ລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2">
            <Video className="size-5 text-primary" aria-hidden />
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">
                {workspaceEnabled ? "ກອງງານຂາຍ" : "Live"}
              </h1>
              {workspaceEnabled && (
                <p className="text-muted-foreground text-sm">
                  ກຽມສິນຄ້າ ລາຄາ ສ່ວນຫຼຸດ ແລະຈໍ ກ່ອນຜູກ Live ຫຼື Feed
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activePages.length === 0 ? (
              <Button
                type="button"
                onClick={() => void handleConnectFacebook()}
              >
                ເຊື່ອມ Facebook
              </Button>
            ) : (
              <>
                <FacebookPageCombobox
                  pages={activePages}
                  value={pageId ?? activePages[0]?.id ?? ""}
                  onValueChange={(v) => setPageId(v)}
                  placeholder="ເລືອກເພຈ"
                  queryKey={["cf-live-pages"]}
                />
                <Button
                  type="button"
                  onClick={() => void handleNewLive()}
                  disabled={createDraft.isPending}
                >
                  {workspaceEnabled ? "ສ້າງງານກຽມຂາຍ" : "ສ້າງ Live"}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1">
            <label
              htmlFor="live-search"
              className="text-muted-foreground text-xs"
            >
              {workspaceEnabled ? "ຄົ້ນຫາງານກຽມ / Live" : "ຄົ້ນຫາ Live"}
            </label>
            <Input
              id="live-search"
              value={searchInput}
              placeholder={
                workspaceEnabled ? "ຊື່ງານ, Live ຫຼື Page" : "ຊື່ Live ຫຼື Page"
              }
              onChange={(event) => {
                setSearchInput(event.target.value);
                debouncedSearch(event.target.value);
              }}
            />
          </div>
          <div className="w-full space-y-1 sm:w-48">
            <span className="text-muted-foreground text-xs">ສະຖານະ</span>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as typeof status)}
            >
              <SelectTrigger>
                <SelectValue placeholder="ທັງໝົດ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ທັງໝົດ</SelectItem>
                <SelectItem value="draft">ຮ່າງ</SelectItem>
                <SelectItem value="live">ກຳລັງ Live</SelectItem>
                <SelectItem value="ended">ຈົບແລ້ວ</SelectItem>
                <SelectItem value="other">ອື່ນໆ</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <QueryState
          isLoading={listQuery.isLoading || pagesQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          isEmpty={!listQuery.data?.items.length}
          description={
            workspaceEnabled
              ? "ຍັງບໍ່ມີງານກຽມຂາຍ ເລີ່ມກຽມສິນຄ້າໄດ້ທີ່ປຸ່ມດ້ານເທິງ"
              : "ຍັງບໍ່ມີ Live"
          }
        >
          <LiveListTable
            items={listQuery.data?.items ?? []}
            warehouseNames={warehouseNames}
          />
        </QueryState>

        <p className="mt-4 text-muted-foreground text-sm">
          <Link
            to="/app/facebook"
            search={{
              facebook: undefined,
              reason: undefined,
              pages: undefined,
              missing: undefined,
              omitted: undefined,
            }}
            className="underline"
          >
            ຈັດການເຊື່ອມ Facebook
          </Link>
        </p>
      </Main>
    </>
  );
}
